import { ICategory, ISubCategory } from "../../../contentful-types"
import React from "react"
import { ArrowLink } from "../../views/atoms/links/ArrowLink"
import { TextLink } from "../../views/atoms/links/TextLink"
import slugify from "slugify"
import styled from "styled-components"
import { colors } from "@carrotfertility/carotene"
import { isGroupSession, isGuide, ReadLearnArticleItem } from "../../../utils/ContentTypes"
import { FormattedMessage } from "react-intl"

type useReadLearnLinksProps = {
  category: ICategory
  subCategory?: ISubCategory
  article?: ReadLearnArticleItem
}

export function useReadLearnLinks({
  category,
  subCategory = null,
  article = null
}: useReadLearnLinksProps): {
  categoryLink: string
  subCategoryLink?: string
  articleLink?: string
  subCategoryPageLink?: string
} {
  const categorySlug = category ? slugify(category?.fields?.navTitle) : null
  const links = {
    categoryLink: `/read/${categorySlug}`,
    subCategoryLink: null,
    articleLink: null,
    subCategoryPageLink: null
  }

  if (subCategory) {
    links.subCategoryLink = `/read/${categorySlug}#${slugify(subCategory?.fields?.navTitle)}`

    links.subCategoryPageLink = `/read/all-articles/${categorySlug}/${slugify(subCategory.fields.navTitle)}`
  }

  if (article && article.fields) {
    const articleSlug = "slug" in article.fields ? article.fields.slug : slugify(article.fields.title)
    if (isGuide(article)) {
      links.articleLink = `/read/${categorySlug}/guide/${articleSlug}`
    } else if (isGroupSession(article)) {
      links.articleLink = `/read/${categorySlug}/group/${articleSlug}`
    } else {
      links.articleLink = `/read/${categorySlug}/${articleSlug}`
    }
  }

  return { ...links }
}

type SubCategoryLinkProps = {
  subCategory: ISubCategory
  category: ICategory
  linkType: "arrow" | "text"
}

export function SubCategoryLink({ subCategory, category, linkType }: SubCategoryLinkProps): JSX.Element {
  const { subCategoryLink } = useReadLearnLinks({ category, subCategory })
  return linkType === "arrow" ? (
    <ArrowLink to={subCategoryLink}>{subCategory?.fields?.title}</ArrowLink>
  ) : (
    <TextLink
      color="black80"
      style={{ fontSize: "14px", paddingRight: "24px", lineHeight: "21px" }}
      underline={false}
      to={subCategoryLink}
    >
      {subCategory?.fields?.title}
    </TextLink>
  )
}

type AdditionalTopicsLinkProps = {
  categoryLink: string
  additionalTopicCount: number
}

export function AdditionalTopicsLink({ categoryLink, additionalTopicCount }: AdditionalTopicsLinkProps): JSX.Element {
  return (
    <TextLink
      style={{ fontSize: "14px", lineHeight: "21px", color: colors.base.black60 }}
      underline={false}
      to={categoryLink}
    >
      <FormattedMessage
        defaultMessage="+ {additionalTopicCount, plural, one {# more topic} other {# more topics}}"
        values={{ additionalTopicCount: additionalTopicCount }}
      />
    </TextLink>
  )
}

export const CategoryLink = styled(TextLink)`
  color: ${colors.base.black80};
  font-size: 20px;
  font-weight: 415;
  border-bottom: none;
  line-height: 30px;
  display: inline-block;
  > svg {
    margin-left: 9px;
    vertical-align: middle;
  }
  &:hover,
  &:focus {
    opacity: 0.5;
    transition: opacity 0.15s ease-in;
  }
`
